import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useParams } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency } from '@/lib/currency';
import { useLocale } from '@/hooks/use-locale';
import { useAuth } from '@/hooks/use-auth';
import ReactDOM from 'react-dom/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter 
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '@/components/ui/tabs';
import { apiRequest } from '@/lib/queryClient';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { InvoicePreview } from '@/components/ui/invoice-preview';
import ClientsPage from './clients';
import { InvoiceEditor } from '@/components/invoice-editor';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import { format, addDays } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { UnifiedFilter } from '@/components/ui/unified-filter';
import { 
  FileText, 
  Printer, 
  Send, 
  Download, 
  Pencil, 
  Edit,
  Trash2,
  MinusCircle,
  Plus,
  Check,
  X,
  FileDown,
  CalendarIcon,
  Filter,
  RefreshCw
} from 'lucide-react';
import QRCode from 'react-qr-code';
import { Client, Invoice, Task } from '@shared/schema';
import { getLocalizedUrl } from '@/lib/localized-routes';

// Definícia typu pre položku faktúry
interface InvoiceItem {
  id?: string;
  name: string;
  price: number;
  quantity: number;
  unit: string;
  vatRate: number;
  total: number;
}

// Zoznam krajín EÚ
const EU_COUNTRIES = [
  'Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic', 'Czechia',
  'Denmark', 'Estonia', 'Finland', 'France', 'Germany', 'Greece', 'Hungary',
  'Ireland', 'Italy', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Netherlands',
  'Poland', 'Portugal', 'Romania', 'Slovakia', 'Slovenia', 'Spain', 'Sweden',
  'Slovensko', 'Česká republika', 'Rakúsko', 'Nemecko', 'Poľsko', 'Maďarsko'
];

/**
 * Určí, či má byť DPH 0% pre zahraničného klienta
 * @param clientCountry - Krajina klienta
 * @param userCountry - Krajina používateľa (predávajúceho)
 * @param clientVatId - IČO DPH klienta (pre B2B v EÚ)
 * @param isUserVatPayer - Či je používateľ platca DPH
 * @returns Objekt s informáciou, či má byť DPH 0% a dôvodom
 */
function shouldApplyZeroVat(
  clientCountry: string | undefined,
  userCountry: string | undefined,
  clientVatId: string | undefined,
  isUserVatPayer: boolean
): { shouldBeZero: boolean; reason: string } {
  // Ak používateľ nie je platca DPH, DPH sa nepočíta
  if (!isUserVatPayer) {
    return { shouldBeZero: false, reason: 'user_not_vat_payer' };
  }

  // Normalizácia krajín (odstránenie diakritiky a konverzia na lowercase)
  const normalizeCountry = (country: string | undefined): string => {
    if (!country) return '';
    return country.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  };

  const normalizedClientCountry = normalizeCountry(clientCountry);
  const normalizedUserCountry = normalizeCountry(userCountry || 'Slovakia');

  // Ak je krajina klienta rovnaká ako krajina používateľa, použije sa štandardné DPH
  if (normalizedClientCountry === normalizedUserCountry) {
    return { shouldBeZero: false, reason: 'same_country' };
  }

  // Ak klient nie je v EÚ, DPH = 0%
  const isClientInEU = EU_COUNTRIES.some(euCountry => 
    normalizeCountry(euCountry) === normalizedClientCountry
  );

  if (!isClientInEU) {
    return { shouldBeZero: true, reason: 'non_eu_client' };
  }

  // Pre klientov v EÚ: ak má IČO DPH (B2B), DPH = 0% (reverse charge)
  // Ak nemá IČO DPH (B2C), použije sa DPH podľa miesta spotreby
  // Pre jednoduchosť implementácie: ak má IČO DPH, použijeme reverse charge (0%)
  // Ak nemá IČO DPH, použijeme štandardné DPH (možno v budúcnosti rozšíriť o DPH podľa krajiny)
  if (clientVatId && clientVatId.trim() !== '') {
    return { shouldBeZero: true, reason: 'eu_b2b_reverse_charge' };
  }

  // Pre B2C v EÚ môžeme použiť štandardné DPH alebo DPH podľa krajiny klienta
  // Pre teraz použijeme štandardné DPH (možno v budúcnosti rozšíriť)
  return { shouldBeZero: false, reason: 'eu_b2c_standard_vat' };
}


// formatCurrency now imported from centralized utility

// Helper function to get status badge color with i18n
const getStatusBadge = (status: string, t: any) => {
  const statusLabels: { [key: string]: string } = {
    'draft': t('invoices:status.draft'),
    'sent': t('invoices:status.sent'), 
    'paid': t('invoices:status.paid'),
    'cancelled': t('invoices:status.cancelled')
  };
  
  switch (status) {
    case 'draft':
      return <Badge variant="outline" style={{ backgroundColor: '#ffffff', color: '#6b7280', borderColor: '#d1d5db' }}>{statusLabels.draft}</Badge>;
    case 'sent':
      return <Badge variant="secondary" style={{ backgroundColor: '#f3f4f6', color: '#374151' }}>{statusLabels.sent}</Badge>;
    case 'paid':
      return <Badge className="bg-green-500 text-white" style={{ backgroundColor: '#10b981', color: '#ffffff' }}>{statusLabels.paid}</Badge>;
    case 'cancelled':
      return <Badge variant="destructive" style={{ backgroundColor: '#ef4444', color: '#ffffff' }}>{statusLabels.cancelled}</Badge>;
    default:
      return <Badge variant="outline" style={{ backgroundColor: '#ffffff', color: '#6b7280', borderColor: '#d1d5db' }}>{statusLabels[status] || status}</Badge>;
  }
};

// InvoiceTable component interface
interface InvoiceTableProps {
  invoices: Invoice[];
  clients: Client[];
  onPreview: (invoice: Invoice) => void;
  onDelete: (invoice: Invoice) => void;
  onEmail: (invoice: Invoice) => void;
  onEdit: (invoice: Invoice) => void;
  onInlineEdit: (invoice: Invoice) => void;
  onMarkAsPaid: (invoiceId: number) => void;
  onMarkAsCancelled: (invoiceId: number) => void;
  onCreateCreditNote: (invoice: Invoice) => void;
  onDuplicate: (invoice: Invoice) => void;
  userData?: any;
}

// InvoiceTable component
function InvoiceTable({ 
  invoices, 
  clients,
  onPreview,
  onDelete,
  onEmail,
  onEdit,
  onInlineEdit,
  onMarkAsPaid,
  onMarkAsCancelled,
  onCreateCreditNote,
  onDuplicate,
  userData
}: InvoiceTableProps) {
  const { t } = useLocale();
  const isVatPayer = userData?.isVatPayer || false;
  
  return (
    <Card className="border border-gray-200">
      <CardHeader className="pb-3 sm:pb-4">
        <CardTitle className="text-lg sm:text-xl">{t('nav:invoices')}</CardTitle>
      </CardHeader>
      <CardContent className="px-3 sm:px-6">
        {invoices.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            <p>Žiadne faktúry</p>
          </div>
        ) : (
          <div className="w-full overflow-x-auto min-w-0">
            <Table className="tk-row w-full table-fixed min-w-[800px]" style={{ tableLayout: 'fixed' }}>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[100px]">{t('invoices:table.invoiceNumber')}</TableHead>
                <TableHead className="min-w-[150px]">{t('invoices:table.client')}</TableHead>
                <TableHead className="min-w-[100px] hidden sm:table-cell">{t('invoices:table.dueDate')}</TableHead>
                <TableHead className="min-w-[100px]">{t('invoices:table.status')}</TableHead>
                <TableHead className="min-w-[100px] hidden md:table-cell">{t('invoices:table.creditNote')}</TableHead>
                <TableHead className="min-w-[100px]">{t('invoices:table.amount')}</TableHead>
                <TableHead className="text-right min-w-[150px]">{t('invoices:table.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.isArray(invoices) ? invoices.map(invoice => {
                // First try to use client data from invoice object, then fall back to clients array
                const invoiceClient = (invoice as any).client;
                const client = invoiceClient || (Array.isArray(clients) ? clients.find(c => c.id === invoice.clientId) : undefined);
                
                // Zobrazíme správnu sumu podľa DPH nastavenia
                let displayAmount = isVatPayer 
                  ? Number(invoice.totalWithTax) 
                  : Number(invoice.totalAmount);
                  
                // Ak je suma 0 alebo NaN, skúsime použiť alternativy
                if (displayAmount === 0 || isNaN(displayAmount)) {
                  displayAmount = Number(invoice.totalWithTax) || Number(invoice.totalAmount) || 0;
                }
                
                // Zabezpečíme, že suma nie je NaN
                if (isNaN(displayAmount)) {
                  displayAmount = 0;
                }
                
                return (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-medium text-sm">
                      <div className="flex flex-col">
                        <span>{invoice.invoiceNumber}</span>
                        <span className="sm:hidden text-xs text-muted-foreground">
                          {format(new Date(invoice.dueDate), 'dd.MM.yyyy')}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="flex flex-col">
                        <span className="font-medium">{client?.businessName || client?.name || 'Neznámy klient'}</span>
                        <div className="md:hidden mt-1">
                          {(invoice as any).hasCreditNote ? (
                            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-xs">
                              {t('invoices:table.credit_note')}
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-sm">{format(new Date(invoice.dueDate), 'dd.MM.yyyy')}</TableCell>
                    <TableCell>
                      {getStatusBadge(invoice.status, t)}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {(invoice as any).hasCreditNote ? (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                          {(invoice as any).creditNoteInfo?.creditNoteNumber || t('invoices:has_credit_note')}
                        </Badge>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium text-sm">
                      {formatCurrency(displayAmount)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1 flex-wrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onPreview(invoice)}
                          title={t('invoices:actions.view')}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        
                        {/* Prepočítanie súm faktúry */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              await apiRequest("POST", `/api/invoices/${invoice.id}/recalculate`);
                              toast({ title: t("invoices:actions.recalculated"), description: t("invoices:actions.recalculatedDescription") });
                              queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
                            } catch (error) {
                              toast({ title: t("common:error"), description: t("invoices:actions.recalculateError") });
                            }
                          }}
                          title={t("invoices:actions.recalculate")}
                        >
                          <RefreshCw className="h-4 w-4 text-orange-500" />
                        </Button>
                        
                        {/* Tlačidlo pre duplikovanie faktúry */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDuplicate(invoice)}
                          title={t('invoices:actions.duplicate')}
                        >
                          <FileDown className="h-4 w-4 text-primary" />
                        </Button>
                        
                        {/* PDF Download Button - temporarily disabled */}
                        {/* <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadPdf(invoice.id, invoice.invoiceNumber || invoice.invoice_number)}
                          title="Stiahnuť ako PDF"
                        >
                          <Download className="h-4 w-4 text-green-600" />
                        </Button> */}
                        
                        {/* Tlačidlo pre znova odoslanie emailu - iba pre odoslané alebo zaplatené faktúry */}
                        {(invoice.status === 'sent' || invoice.status === 'paid') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEmail(invoice)}
                            title={t('invoices:actions.send_email')}
                          >
                            <Send className="h-4 w-4" style={{ color: '#f59e0b' }} />
                          </Button>
                        )}
                        
                        {/* Tlačidlo pre dobropis - iba pre odoslané alebo zaplatené faktúry bez existujúceho dobropisu */}
                        {(invoice.status === 'sent' || invoice.status === 'paid') && !(invoice as any).hasCreditNote && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onCreateCreditNote(invoice)}
                            title={t('invoices:actions.create_credit_note')}
                          >
                            <MinusCircle className="h-4 w-4" style={{ color: '#16a34a' }} />
                          </Button>
                        )}
                        
                        {invoice.status === 'draft' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onInlineEdit(invoice)}
                              title={t('invoices:actions.quick_edit')}
                              className="text-primary hover:text-primary/80"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onEdit(invoice)}
                              title={t('invoices:actions.edit')}
                            >
                              <Pencil className="h-4 w-4" style={{ color: '#16a34a' }} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onEmail(invoice)}
                              title={t('invoices:actions.send_email')}
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onDelete(invoice)}
                              title={t('invoices:actions.delete')}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </>
                        )}
                        
                        {invoice.status === 'sent' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onMarkAsPaid(invoice.id)}
                              title={t('invoices:actions.mark_as_paid')}
                            >
                              <Check className="h-4 w-4" style={{ color: '#16a34a' }} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onMarkAsCancelled(invoice.id)}
                              title={t('invoices:actions.mark_as_cancelled')}
                            >
                              <X className="h-4 w-4 text-red-500" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              }) : null}
            </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Invoices() {
  const { t } = useLocale();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const params = useParams<{ id?: string }>();
  const invoiceIdFromUrl = params?.id ? parseInt(params.id, 10) : null;
  const { user } = useAuth();
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [isPrintingToPdf, setIsPrintingToPdf] = useState(false);
  const invoicePreviewRef = useRef<HTMLIFrameElement>(null);
  
  // Inline editor state
  const [isInlineEditorOpen, setIsInlineEditorOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  
  // Credit note creation state
  const [isCreditNoteDialogOpen, setIsCreditNoteDialogOpen] = useState(false);
  const [creditNoteInvoice, setCreditNoteInvoice] = useState<Invoice | null>(null);
  const [creditNoteItems, setCreditNoteItems] = useState<any[]>([]);
  const [creditNoteReason, setCreditNoteReason] = useState('storno_objednavky');
  const [creditNoteReasonText, setCreditNoteReasonText] = useState('');
  const [creditNoteNotes, setCreditNoteNotes] = useState('');
  
  // Invoice detail from URL - not found state
  const [invoiceNotFoundFromUrl, setInvoiceNotFoundFromUrl] = useState(false);

  // Filter states
  const [filterClientId, setFilterClientId] = useState<string>('');
  const [filterDateFrom, setFilterDateFrom] = useState<Date | undefined>();
  const [filterDateTo, setFilterDateTo] = useState<Date | undefined>();
  
  // Client creation states
  const [isCreateClientDialogOpen, setIsCreateClientDialogOpen] = useState(false);
  const [isClientPageOpen, setIsClientPageOpen] = useState(false);
  const [newClientData, setNewClientData] = useState({
    businessName: '',
    name: '',
    email: '',
    businessId: '',
    taxId: '',
    vatId: '',
    street: '',
    city: '',
    postalCode: '',
    hourlyRate: ''
  });

  // Custom invoice items state
  const [invoiceItems, setInvoiceItems] = useState<any[]>([]);
  
  // Simple invoice query - load all invoices at once
  const {
    data: invoicesResponse,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['/api/invoices'],
    enabled: !!user,
    queryFn: async () => {
      console.log('🚀 Making API call to /api/invoices');
      const response = await fetch('/api/invoices', {
        credentials: 'include',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        }
      });
      
      console.log('📡 Response status:', response.status);
      
      if (!response.ok) {
        console.error('❌ API call failed:', response.status, response.statusText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('📊 Invoices API response:', data);
      console.log('📊 Data count:', data?.data?.length);
      return data;
    },
    staleTime: 1000, // 1 sekunda - dáta sa považujú za čerstvé 1 sekundu
    gcTime: 5 * 60 * 1000, // 5 minút - cache sa vymaže po 5 minútach nečinnosti
    refetchOnWindowFocus: false, // Neprefetchovať pri návrate na okno
    refetchOnMount: true, // Refetch pri mount
  });

  // Extract invoices from response (API returns {data: [...], pagination: {...}})
  const invoices = invoicesResponse?.data || [];
  console.log('📋 Processed invoices:', invoices.length, 'items');
  console.log('📋 invoicesResponse:', invoicesResponse);
  
  // Debug log to check what we receive
  useEffect(() => {
    console.log('📊 invoicesResponse:', invoicesResponse);
    console.log('📊 invoices array:', invoices);
    console.log('📊 filteredInvoices:', filteredInvoices);
    console.log('📊 currentInvoices:', currentInvoices);
  }, [invoicesResponse, invoices]);

  // Fetch single invoice when we have id in URL (more reliable than searching in paginated list)
  const { data: invoiceFromUrl, isLoading: isLoadingInvoiceFromUrl, error: invoiceFromUrlError } = useQuery({
    queryKey: ['/api/invoices', invoiceIdFromUrl],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/invoices/${invoiceIdFromUrl}`);
      return res.json();
    },
    enabled: !!invoiceIdFromUrl && !!Number.isFinite(invoiceIdFromUrl) && !!user,
  });

  // Open invoice preview when we have id in URL and invoice is loaded
  useEffect(() => {
    if (!invoiceIdFromUrl || !Number.isFinite(invoiceIdFromUrl)) return;
    if (isLoadingInvoiceFromUrl) return;
    if (invoiceFromUrlError) {
      setInvoiceNotFoundFromUrl(true);
      return;
    }
    if (invoiceFromUrl) {
      setSelectedInvoice(invoiceFromUrl);
      setIsPreviewDialogOpen(true);
      setInvoiceNotFoundFromUrl(false);
    }
  }, [invoiceIdFromUrl, invoiceFromUrl, isLoadingInvoiceFromUrl, invoiceFromUrlError]);

  useEffect(() => {
    if (!invoiceIdFromUrl) setInvoiceNotFoundFromUrl(false);
  }, [invoiceIdFromUrl]);

  // Show toast when redirected with sendFailed=1 (from Quick Task flow)
  const sendFailedShownRef = useRef(false);
  useEffect(() => {
    const search = typeof window !== 'undefined' ? window.location.search : '';
    if (search.includes('sendFailed=1') && invoiceIdFromUrl && selectedInvoice && !sendFailedShownRef.current) {
      sendFailedShownRef.current = true;
      toast({
        title: "Faktúra vytvorená",
        description: "Nepodarilo sa odoslať email. Použite tlačidlo „Odoslať emailom“ nižšie.",
        variant: "destructive",
      });
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [invoiceIdFromUrl, selectedInvoice, toast]);

  // Fetch clients
  const { data: clients } = useQuery({
    queryKey: ['/api/clients'],
    enabled: !!user,
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/clients');
      const data = await response.json();
      return data.items || data;
    }
  });

  // Fetch user data for VAT settings
  const { data: userData } = useQuery({
    queryKey: ['/api/user'],
    enabled: !!user,
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/user');
      return res.json();
    }
  });

  // Fetch non-invoiced tasks
  const { data: nonInvoicedTasks, refetch: refetchNonInvoicedTasks } = useQuery({
    queryKey: ['/api/tasks/non-invoiced', selectedClientId],
    queryFn: async () => {
      const url = selectedClientId 
        ? `/api/tasks/non-invoiced?clientId=${selectedClientId}` 
        : '/api/tasks/non-invoiced';
      const res = await apiRequest('GET', url);
      const data = await res.json();
      return data.items || data;
    },
    enabled: !!user && (isCreateDialogOpen || isEditDialogOpen)
  });

  // Fetch invoice tasks
  const { data: invoiceTasks, refetch: refetchInvoiceTasks } = useQuery({
    queryKey: ['/api/invoices', selectedInvoice?.id, 'tasks'],
    queryFn: async () => {
      if (!selectedInvoice) return [];
      const res = await apiRequest('GET', `/api/invoices/${selectedInvoice.id}/tasks`);
      return res.json() as Promise<Task[]>;
    },
    enabled: !!user && !!selectedInvoice
  });
  
  // Fetch credit notes for invoice
  const { data: invoiceCreditNotes } = useQuery({
    queryKey: ['/api/credit-notes/by-invoice', selectedInvoice?.id],
    queryFn: async () => {
      if (!selectedInvoice) return [];
      const res = await apiRequest('GET', `/api/credit-notes/by-invoice/${selectedInvoice.id}`);
      return res.json() as Promise<any[]>;
    },
    enabled: !!user && !!selectedInvoice && isPreviewDialogOpen
  });
  
  // Set selected task IDs when invoice tasks are loaded (for editing invoice)
  useEffect(() => {
    if (isEditDialogOpen && invoiceTasks && invoiceTasks.length > 0) {
      setSelectedTaskIds(invoiceTasks.map(task => task.id));
    }
  }, [isEditDialogOpen, invoiceTasks]);

  // Delete invoice mutation
  const deleteMutation = useMutation({
    mutationFn: async (invoiceId: number) => {
      try {
        const response = await apiRequest('DELETE', `/api/invoices/${invoiceId}`);
        console.log('🗑️ [DELETE] API response status:', response.status);
        
        // 204 No Content je úspešná odpoveď pre DELETE
        if (response.status === 204 || response.ok) {
          return { success: true, invoiceId };
        }
        
        // Ak nie je 204, skúsime načítať error message
        let errorMessage = 'Nepodarilo sa zmazať faktúru';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // Ak nie je JSON, použijeme status text
          errorMessage = response.statusText || errorMessage;
        }
        
        throw new Error(errorMessage);
      } catch (error: any) {
        console.error('🗑️ [DELETE] Error in mutationFn:', error);
        throw error;
      }
    },
    onSuccess: async (data, invoiceId) => {
      console.log('🗑️ [DELETE] Invoice deleted successfully, invoiceId:', invoiceId);
      
      // Zatvoríme dialog najprv
      setDeleteDialogOpen(false);
      setSelectedInvoice(null);
      
      // Optimisticky odstránime faktúru z cache okamžite - to zaručí okamžitý feedback
      queryClient.setQueryData(['/api/invoices'], (old: any) => {
        if (!old || !old.data) {
          console.log('🗑️ [DELETE] No old data to filter');
          return old;
        }
        const filteredData = old.data.filter((inv: any) => inv.id !== invoiceId);
        console.log('🗑️ [DELETE] Optimistically filtered invoices:', filteredData.length, 'from', old.data.length);
        return {
          ...old,
          data: filteredData,
          pagination: {
            ...old.pagination,
            total: Math.max(0, (old.pagination?.total || old.data.length) - 1),
            totalCount: Math.max(0, filteredData.length)
          }
        };
      });
      
      // Invalidujeme queries - to označí dáta ako zastaralé
      queryClient.invalidateQueries({ 
        queryKey: ['/api/invoices'],
        exact: true
      });
      
      // Vynútíme refetch v pozadí - to zaručí že sa načítajú najnovšie dáta zo servera
      // Použijeme setTimeout aby sa refetch stihol po invalidácii
      setTimeout(async () => {
        try {
          await queryClient.refetchQueries({ 
            queryKey: ['/api/invoices'],
            exact: true,
            type: 'active'
          });
          console.log('🗑️ [DELETE] Cache refetched from server');
        } catch (error) {
          console.error('🗑️ [DELETE] Error refetching:', error);
        }
      }, 50);
      
      console.log('🗑️ [DELETE] Cache updated optimistically and invalidated');
      
      toast({
        title: t('invoices:messages.deleted_title'),
        description: t('invoices:messages.deleted_desc'),
      });
    },
    onError: (error: Error) => {
      console.error('🗑️ [DELETE] Error deleting invoice:', error);
      console.error('🗑️ [DELETE] Error message:', error.message);
      console.error('🗑️ [DELETE] Error stack:', error.stack);
      
      // Ak dôjde k chybe, invalidujeme cache aby sa načítali správne dáta
      queryClient.invalidateQueries({ queryKey: ['/api/invoices'] });
      
      const errorMessage = error.message || 'Neznáma chyba';
      toast({
        title: t('common:error'),
        description: t('invoices.messages.delete_error', { error: errorMessage }),
        variant: 'destructive',
      });
    }
  });

  // Update invoice status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ 
      invoiceId, 
      status, 
      email 
    }: { 
      invoiceId: number; 
      status: string; 
      email?: string 
    }) => {
      return apiRequest('POST', `/api/invoices/${invoiceId}/status`, { status, email });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/invoices'] });
      
      // Rozlišujeme medzi odoslaním emailom a len zmenou statusu
      if (variables.status === 'sent' && variables.email) {
        toast({
          title: t('invoices:messages.emailSent', 'Faktúra odoslaná'),
          description: t('invoices:messages.emailSentDescription', `Faktúra bola úspešne odoslaná na email ${variables.email}`),
        });
      } else {
        toast({
          title: t('invoices:messages.statusUpdated', 'Status faktúry aktualizovaný'),
          description: t('invoices:messages.statusUpdatedDescription', 'Status faktúry bol úspešne zmenený'),
        });
      }
      setIsEmailDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: t('common:error', 'Chyba'),
        description: t('invoices.messages.statusUpdateError', 'Nepodarilo sa zmeniť status faktúry: {error}', { error: error.message }) || `Nepodarilo sa zmeniť status faktúry: ${error.message}`,
        variant: 'destructive',
      });
    }
  });
  
  // Update invoice mutation
  const updateInvoiceMutation = useMutation({
    mutationFn: async ({ 
      invoiceId, 
      invoiceData, 
      taskIds 
    }: { 
      invoiceId: number;
      invoiceData: Partial<Invoice>; 
      taskIds: number[] 
    }) => {
      return apiRequest('PUT', `/api/invoices/${invoiceId}`, { invoiceData, taskIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/invoices'] });
      queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
      queryClient.invalidateQueries({ queryKey: ['/api/tasks/statistics'] });
      toast({
        title: t('invoices:messages.updated_title'),
        description: t('invoices:messages.updated_desc'),
      });
      setIsEditDialogOpen(false);
      setSelectedTaskIds([]);
      setSelectedClientId(null);
    },
    onError: (error: Error) => {
      toast({
        title: t('common:error'),
        description: t('invoices.messages.update_error', { error: error.message }),
        variant: 'destructive',
      });
    }
  });

  // Create invoice mutation
  const createInvoiceMutation = useMutation({
    mutationFn: async (data: { 
      invoiceData: Partial<Invoice>; 
      taskIds: number[] 
    }) => {
      return apiRequest('POST', '/api/invoices', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/invoices'] });
      queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
      queryClient.invalidateQueries({ queryKey: ['/api/tasks/statistics'] });
      toast({
        title: t('invoices:messages.created_title'),
        description: t('invoices:messages.created_desc'),
      });
      setIsCreateDialogOpen(false);
      setSelectedTaskIds([]);
      setSelectedClientId(null);
    },
    onError: (error: Error) => {
      toast({
        title: t('common:error'),
        description: t('invoices.messages.create_error', { error: error.message }),
        variant: 'destructive',
      });
    }
  });

  // Create client mutation
  const createClientMutation = useMutation({
    mutationFn: async (clientData: any) => {
      return apiRequest('POST', '/api/clients', clientData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/clients'] });
      toast({
        title: t('clients:messages.created_title'),
        description: t('clients:messages.created_desc'),
      });
      setIsCreateClientDialogOpen(false);
      // Reset form
      setNewClientData({
        businessName: '',
        name: '',
        email: '',
        businessId: '',
        taxId: '',
        vatId: '',
        street: '',
        city: '',
        postalCode: '',
        hourlyRate: ''
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('common:error'),
        description: `${t('clients:createFailed')}: ${error.message}`,
        variant: 'destructive',
      });
    }
  });

  // Handle PDF download
  const handleDownloadPdf = async (invoiceId: number, invoiceNumber?: string) => {
    try {
      const response = await fetch(`/api/invoices/${invoiceId}/pdf`, {
        method: 'GET',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      // Get the PDF blob
      const pdfBlob = await response.blob();
      
      // Get invoice number if not provided
      let invoiceNum = invoiceNumber;
      if (!invoiceNum) {
        const invoice = invoices.find((inv: any) => inv.id === invoiceId);
        invoiceNum = invoice?.invoiceNumber || invoice?.invoice_number || `#${invoiceId}`;
      }
      
      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceNum}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: t("invoices:actions.pdfDownloaded") || "PDF stiahnuté",
        description: t("invoices:actions.pdfDownloadedDescription", { id: invoiceNum }) || `Faktúra ${invoiceNum} bola úspešne stiahnutá ako PDF`,
      });
    } catch (error) {
      console.error('PDF download error:', error);
      toast({
        title: t("invoices:actions.pdfDownloadError") || "Chyba",
        description: t("invoices:actions.pdfDownloadErrorDescription", { error: error instanceof Error ? error.message : 'Neznáma chyba' }) || `Nepodarilo sa stiahnuť PDF: ${error instanceof Error ? error.message : 'Neznáma chyba'}`,
        variant: 'destructive',
      });
    }
  };

  const calculateInvoiceTotals = (taskIds: number[], tasksSource: Task[] = [], clientId?: number | null) => {
    // Filter tasks based on provided IDs
    const tasksToInvoice = tasksSource.filter(task => 
      taskIds.includes(task.id)
    );
    
    // Calculate total amount based on task types
    const totalAmount = tasksToInvoice.reduce((sum, task) => {
      let taskAmount = 0;
      
      if (task.type === "cash" || task.type === "settlement") {
        // Pre hotovosť a zápočet použijeme manuálnu sumu
        taskAmount = task.manualAmount ? parseFloat(task.manualAmount.toString()) : 0;
        // Pre zápočty už máme záporné sumy v databáze, takže ich používame tak ako sú
      } else {
        // Pre štandardné úlohy a bonusy použijeme štandardný výpočet
        const client = Array.isArray(clients) ? clients.find(c => c.id === task.clientId) : undefined;
        const rate = client ? Number(client.hourlyRate) : 0;
        const hours = parseFloat(task.hoursWorked || '0');
        taskAmount = rate * (isNaN(hours) ? 0 : hours);
      }
      
      // Zabezpečíme, že suma nie je NaN
      if (isNaN(taskAmount)) {
        taskAmount = 0;
      }
      
      return sum + taskAmount;
    }, 0);
    
    // Get the user data from query client
    const userData = queryClient.getQueryData<any>(['/api/user']);
    const isVatPayer = userData?.isVatPayer || false;
    const vatRate = userData?.companyVatRate || 20;
    
    // Get client data if clientId is provided
    const selectedClient = clientId && Array.isArray(clients) 
      ? clients.find(c => c.id === clientId) 
      : undefined;
    
    // Check if VAT should be zero for foreign clients
    const vatCheck = shouldApplyZeroVat(
      selectedClient?.country,
      userData?.companyCountry,
      selectedClient?.vatId,
      isVatPayer
    );
    
    // Calculate tax only if the user is a VAT payer and VAT should not be zero
    const totalTax = (isVatPayer && !vatCheck.shouldBeZero) ? totalAmount * (vatRate / 100) : 0;
    const totalWithTax = totalAmount + totalTax;
    
    // Zabezpečíme, že všetky sumy nie sú NaN
    const safeTotalAmount = isNaN(totalAmount) ? 0 : totalAmount;
    const safeTotalTax = isNaN(totalTax) ? 0 : totalTax;
    const safeTotalWithTax = isNaN(totalWithTax) ? 0 : totalWithTax;
    
    return { 
      totalAmount: safeTotalAmount, 
      totalTax: safeTotalTax, 
      totalWithTax: safeTotalWithTax, 
      tasksToInvoice,
      vatReason: vatCheck.reason,
      shouldBeZeroVat: vatCheck.shouldBeZero
    };
  };
  
  // State pre hodnoty formulára
  const [issueDateValue, setIssueDateValue] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [deliveryDateValue, setDeliveryDateValue] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [dueDateValue, setDueDateValue] = useState(format(addDays(new Date(), 14), 'yyyy-MM-dd'));
  const [paymentMethodValue, setPaymentMethodValue] = useState("prevod");
  
  const [currentItem, setCurrentItem] = useState<any>({
    name: '',
    price: 0,
    quantity: 1,
    unit: 'ks',
    vatRate: 20,
    total: 0
  });

  const handleCreateInvoice = () => {
    if (!selectedClientId || (selectedTaskIds.length === 0 && invoiceItems.length === 0)) {
      toast({
        title: t('common:error'),
        description: t('invoices:selectClientAndTasks'),
        variant: 'destructive',
      });
      return;
    }
    
    // Get user data for VAT check
    const userData = queryClient.getQueryData<any>(['/api/user']);
    const isVatPayer = userData?.isVatPayer || false;
    const selectedClient = Array.isArray(clients) ? clients.find(c => c.id === selectedClientId) : undefined;
    
    // Check if VAT should be zero for foreign clients
    const vatCheck = shouldApplyZeroVat(
      selectedClient?.country,
      userData?.companyCountry,
      selectedClient?.vatId,
      isVatPayer
    );
    
    // Calculate totals from tasks
    const { totalAmount: taskTotalAmount, totalTax: taskTotalTax, totalWithTax: taskTotalWithTax } = 
      calculateInvoiceTotals(selectedTaskIds, nonInvoicedTasks, selectedClientId);
    
    // Calculate totals from custom items
    let customItemsTotal = 0;
    let customItemsTax = 0;
    
    invoiceItems.forEach(item => {
      customItemsTotal += item.total;
      // Only calculate VAT if user is VAT payer and VAT should not be zero
      if (isVatPayer && !vatCheck.shouldBeZero) {
        customItemsTax += (item.total * item.vatRate / 100);
      }
    });
    
    // Combine totals
    const totalAmount = taskTotalAmount + customItemsTotal;
    const totalTax = (isVatPayer && !vatCheck.shouldBeZero) ? (taskTotalTax + customItemsTax) : 0;
    const totalWithTax = totalAmount + totalTax;
    
    // Použitie hodnôt z formulára
    const issueDate = new Date(issueDateValue);
    const deliveryDate = new Date(deliveryDateValue);
    const dueDate = new Date(dueDateValue);

    // Zostavenie informácií o platbe
    let paymentDetailsText = '';
    switch (paymentMethodValue) {
      case 'prevod':
        const iban = userData?.bankAccount || 'N/A';
        const accountNumber = userData?.accountNumber || 'N/A';
        paymentDetailsText = `${t('invoices:payment.method')}: ${t('invoices:payment.transfer')}\nIBAN: ${iban}\n${t('invoices:payment.accountNumber')}: ${accountNumber}`;
        break;
      case 'karta':
        paymentDetailsText = `${t('invoices:payment.method')}: ${t('invoices:payment.card')}`;
        break;
      case 'hotovost':
        paymentDetailsText = `${t('invoices:payment.method')}: ${t('invoices:payment.cash')}`;
        break;
      case 'dobierka':
        paymentDetailsText = `${t('invoices:payment.method')}: ${t('invoices:payment.cod')}`;
        break;
      case 'zapoctom':
        paymentDetailsText = `${t('invoices:payment.method')}: ${t('invoices:payment.credit')}`;
        break;
      case 'zlozenka':
        paymentDetailsText = `${t('invoices:payment.method')}: ${t('invoices:payment.postalOrder')}`;
        break;
      default:
        paymentDetailsText = `${t('invoices:payment.method')}: ${t('invoices:payment.transfer')}`;
    }

    // Prepare custom items for storage in displayItems
    const customItemsForStorage = invoiceItems.map(item => ({
      ...item,
      type: 'custom',
    }));

    const invoiceData = {
      clientId: selectedClientId,
      // invoiceNumber will be generated by backend
      status: 'draft' as 'draft' | 'sent' | 'paid' | 'cancelled',
      issueDate: issueDate.toISOString(), // Convert Date to ISO string
      dueDate: dueDate.toISOString(), // Convert Date to ISO string
      totalAmount: totalAmount.toString(), // Convert to string for DB storage
      totalTax: totalTax.toString(),        // Convert to string for DB storage
      totalWithTax: totalWithTax.toString(), // Convert to string for DB storage
      notes: '',
      paymentMethod: paymentMethodValue,
      deliveryDate: deliveryDate.toISOString(), // Convert Date to ISO string
      paymentDetails: paymentDetailsText,
      displayItems: customItemsForStorage.length > 0 ? customItemsForStorage : undefined
    };

    createInvoiceMutation.mutate({ 
      invoiceData, 
      taskIds: selectedTaskIds 
    });
  };
  
  const handleUpdateInvoice = () => {
    if (!selectedInvoice || !selectedClientId || (selectedTaskIds.length === 0 && invoiceItems.length === 0)) {
      toast({
        title: 'Chyba',
        description: 'Vyberte klienta a aspoň jednu úlohu alebo pridajte vlastnú položku.',
        variant: 'destructive',
      });
      return;
    }
    
    // Get user data for VAT check
    const userData = queryClient.getQueryData<any>(['/api/user']);
    const isVatPayer = userData?.isVatPayer || false;
    const selectedClient = Array.isArray(clients) ? clients.find(c => c.id === selectedClientId) : undefined;
    
    // Check if VAT should be zero for foreign clients
    const vatCheck = shouldApplyZeroVat(
      selectedClient?.country,
      userData?.companyCountry,
      selectedClient?.vatId,
      isVatPayer
    );
    
    // For editing, we need to combine existing invoice tasks with potentially newly selected tasks
    const allAvailableTasks = [...(invoiceTasks || []), ...(nonInvoicedTasks || [])];
    const { totalAmount: taskTotalAmount, totalTax: taskTotalTax, totalWithTax: taskTotalWithTax } = 
      calculateInvoiceTotals(selectedTaskIds, allAvailableTasks, selectedClientId);
    
    // Calculate totals from custom items
    let customItemsTotal = 0;
    let customItemsTax = 0;
    
    invoiceItems.forEach(item => {
      customItemsTotal += item.total;
      // Only calculate VAT if user is VAT payer and VAT should not be zero
      if (isVatPayer && !vatCheck.shouldBeZero) {
        customItemsTax += (item.total * item.vatRate / 100);
      }
    });
    
    // Combine totals
    const totalAmount = taskTotalAmount + customItemsTotal;
    const totalTax = (isVatPayer && !vatCheck.shouldBeZero) ? (taskTotalTax + customItemsTax) : 0;
    const totalWithTax = totalAmount + totalTax;
    
    // Prepare custom items for storage in displayItems
    const customItemsForStorage = invoiceItems.map(item => ({
      ...item,
      type: 'custom',
    }));
    
    const invoiceData = {
      totalAmount: totalAmount.toString(),
      totalTax: totalTax.toString(),
      totalWithTax: totalWithTax.toString(),
      displayItems: customItemsForStorage.length > 0 ? customItemsForStorage : undefined
    };
    
    updateInvoiceMutation.mutate({
      invoiceId: selectedInvoice.id,
      invoiceData,
      taskIds: selectedTaskIds
    });
  };

  const handleEmailSend = (invoiceId: number) => {
    updateStatusMutation.mutate({
      invoiceId,
      status: 'sent',
      email: emailAddress
    });
  };

  const handleClientChange = (clientId: string) => {
    setSelectedClientId(Number(clientId));
    setSelectedTaskIds([]);
    refetchNonInvoicedTasks();
  };

  const handleDeleteClick = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setDeleteDialogOpen(true);
  };

  const handlePreviewClick = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    refetchInvoiceTasks();
    setIsPreviewDialogOpen(true);
  };

  const handleEmailClick = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    // Pre-fill with client's invoice email if available
    const client = Array.isArray(clients) ? clients.find(c => c.id === invoice.clientId) : undefined;
    // Použi primárny email klienta; ak chýba, fallback na fakturačný
    setEmailAddress((client as any)?.email || (client as any)?.invoiceEmail || '');
    setIsEmailDialogOpen(true);
  };
  
  // Handle edit invoice
  const handleEditClick = (invoice: Invoice) => {
    if (invoice.status !== 'draft') {
      toast({
        title: 'Úprava faktúry',
        description: 'Upravovať je možné len faktúry v stave konceptu.',
        variant: 'destructive'
      });
      return;
    }
    
    setSelectedInvoice(invoice);
    setSelectedClientId(invoice.clientId);
    refetchInvoiceTasks();
    
    // Load custom items if they exist in the invoice
    if (invoice.displayItems && Array.isArray(invoice.displayItems)) {
      // Filter only custom items
      const customItems = invoice.displayItems
        .filter(item => item.type === 'custom')
        .map(item => ({
          id: item.id || Date.now().toString(),
          name: item.name || '',
          price: typeof item.price === 'number' ? item.price : 0,
          quantity: typeof item.quantity === 'number' ? item.quantity : 1,
          unit: item.unit || 'ks',
          vatRate: typeof item.vatRate === 'number' ? item.vatRate : 20,
          total: typeof item.total === 'number' ? item.total : 0
        }));
      
      setInvoiceItems(customItems);
    } else {
      // Clear any existing items if there are none in the invoice
      setInvoiceItems([]);
    }
    
    // We'll need to set the selected task IDs after fetching invoice tasks
    // This will happen in the useEffect below
    
    setIsEditDialogOpen(true);
  };

  const handleMarkAsPaid = (invoiceId: number) => {
    updateStatusMutation.mutate({
      invoiceId,
      status: 'paid'
    });
  };

  // Vytvoriť dobropis handler - otvorí modal pre výber položiek
  const handleCreateCreditNote = async (invoice: Invoice) => {
    console.log('🔧 [CreditNote] Opening dialog for invoice:', invoice);
    console.log('🔧 [CreditNote] Invoice displayItems:', invoice.displayItems);
    console.log('🔧 [CreditNote] Invoice display_items:', invoice.display_items);
    
    setCreditNoteInvoice(invoice);
    
    // Načítame položky faktúry - skúsime rôzne formáty
    let invoiceItems: any[] = [];
    
    // Skúsime displayItems (camelCase)
    if (invoice.displayItems && Array.isArray(invoice.displayItems)) {
      invoiceItems = invoice.displayItems;
    }
    // Skúsime display_items (snake_case)
    else if (invoice.display_items && Array.isArray(invoice.display_items)) {
      invoiceItems = invoice.display_items;
    }
    // Skúsime načítať z API, ak nemáme položky
    else {
      console.log('🔧 [CreditNote] No displayItems found, fetching from API...');
      try {
        const response = await apiRequest('GET', `/api/invoices/${invoice.id}`);
        const fullInvoice = await response.json();
        console.log('🔧 [CreditNote] Full invoice from API:', fullInvoice);
        invoiceItems = fullInvoice.displayItems || fullInvoice.display_items || [];
      } catch (error) {
        console.error('🔧 [CreditNote] Error fetching invoice:', error);
        toast({
          title: "Chyba",
          description: "Nepodarilo sa načítať položky faktúry",
          variant: "destructive",
        });
        return;
      }
    }
    
    console.log('🔧 [CreditNote] Invoice items found:', invoiceItems.length);
    
    // Ak nemáme žiadne položky, upozorníme používateľa
    if (invoiceItems.length === 0) {
      toast({
        title: "Upozornenie",
        description: "Faktúra nemá žiadne položky na dobropis",
        variant: "default",
      });
      return;
    }
    
    const itemsWithSelection = invoiceItems.map((item: any, index: number) => {
      // Rôzne formáty položiek
      const name = item.name || item.description || item.title || '';
      const description = item.description || '';
      const quantity = item.quantity || item.qty || item.amount || 1;
      const unitPrice = item.unitPrice || item.unit_price || item.price || item.unitPrice || 0;
      const vatRate = item.vatRate || item.vat_rate || item.taxRate || (user?.companyVatRate ? parseFloat(user.companyVatRate.toString()) : 20);
      const amount = quantity * unitPrice;
      
      return {
        id: item.id || `item-${index}`,
        invoice_item_id: item.id || null,
        name: name,
        description: description,
        quantity: quantity,
        unit_price: unitPrice,
        vat_rate: vatRate,
        amount: amount,
        vat_amount: 0,
        total_amount: 0,
        original_quantity: quantity,
        original_amount: amount,
        selected: false,
        credit_quantity: 0, // Množstvo na dobropis
      };
    });
    
    // Vypočítame DPH pre každú položku
    itemsWithSelection.forEach((item: any) => {
      const isVatPayer = user?.isVatPayer === true;
      if (isVatPayer) {
        item.vat_amount = item.amount * (item.vat_rate / 100);
      } else {
        item.vat_amount = 0;
      }
      item.total_amount = item.amount + item.vat_amount;
    });
    
    console.log('🔧 [CreditNote] Items with selection:', itemsWithSelection);
    
    setCreditNoteItems(itemsWithSelection);
    setCreditNoteReason('storno_objednavky');
    setCreditNoteReasonText(`Dobropis k faktúre č. ${invoice.invoiceNumber || invoice.invoice_number}`);
    setCreditNoteNotes('');
    setIsCreditNoteDialogOpen(true);
  };

  // Inline edit handler
  const handleInlineEdit = (invoice: Invoice) => {
    if (invoice.status !== 'draft') {
      toast({
        title: 'Úprava faktúry',
        description: 'Upravovať je možné len faktúry v stave konceptu.',
        variant: 'destructive'
      });
      return;
    }
    
    setEditingInvoice(invoice);
    setIsInlineEditorOpen(true);
  };

  // Mutácia pre vytvorenie čiastočného dobropisu s položkami
  const createCreditNoteMutation = useMutation({
    mutationFn: async (data: {
      invoice_id: number;
      issue_date: Date;
      due_date?: Date;
      reason_type: string;
      reason: string;
      notes?: string;
      items: any[];
    }) => {
      console.log('🔧 [Invoices] Creating partial credit note:', data);
      const response = await apiRequest("POST", `/api/credit-notes/create`, {
        invoice_id: data.invoice_id,
        issue_date: data.issue_date.toISOString(),
        due_date: data.due_date?.toISOString(),
        reason_type: data.reason_type,
        reason: data.reason,
        notes: data.notes || '',
        items: data.items,
      });
      const result = await response.json();
      console.log('🔧 [Invoices] Credit note creation response:', result);
      return result;
    },
    onSuccess: (newCreditNote) => {
      console.log('🔧 [Invoices] Credit note created successfully:', newCreditNote);
      queryClient.invalidateQueries({ queryKey: ["/api/dobropisy"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/credit-notes"] });
      setIsCreditNoteDialogOpen(false);
      setCreditNoteInvoice(null);
      setCreditNoteItems([]);
      toast({
        title: "Dobropis vytvorený",
        description: `Dobropis č. ${newCreditNote.credit_note_number || newCreditNote.creditNoteNumber} bol úspešne vytvorený`,
      });
    },
    onError: (error: any) => {
      console.error('🔧 [Invoices] Credit note creation error:', error);
      const errorMessage = error.message || error.errors?.[0]?.message || "Nepodarilo sa vytvoriť dobropis";
      toast({
        title: "Chyba pri vytváraní dobropisu",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
  
  // Handler pre vytvorenie dobropisu z modalu
  const handleSubmitCreditNote = () => {
    if (!creditNoteInvoice) return;
    
    // Filtrujeme len vybrané položky s množstvom > 0
    const selectedItems = creditNoteItems
      .filter((item: any) => item.selected && item.credit_quantity > 0)
      .map((item: any) => {
        // Vypočítame sumy na základe dobropisovaného množstva
        const creditQty = item.credit_quantity;
        const unitPrice = item.unit_price;
        const vatRate = item.vat_rate;
        const amount = creditQty * unitPrice;
        const vatAmount = user?.isVatPayer === true ? amount * (vatRate / 100) : 0;
        const totalAmount = amount + vatAmount;
        
        return {
          invoice_item_id: item.invoice_item_id,
          name: item.name,
          description: item.description || '',
          quantity: creditQty,
          unit_price: unitPrice,
          vat_rate: vatRate,
          amount: amount,
          vat_amount: vatAmount,
          total_amount: totalAmount,
          original_quantity: item.original_quantity,
          original_amount: item.original_amount,
        };
      });
    
    if (selectedItems.length === 0) {
      toast({
        title: "Chyba",
        description: "Vyberte aspoň jednu položku na dobropis",
        variant: "destructive",
      });
      return;
    }
    
    if (!creditNoteReasonText.trim()) {
      toast({
        title: "Chyba",
        description: "Zadajte dôvod dobropisu",
        variant: "destructive",
      });
      return;
    }
    
    const issueDate = new Date();
    const dueDate = creditNoteInvoice.dueDate 
      ? new Date(creditNoteInvoice.dueDate)
      : new Date(issueDate.getTime() + 14 * 24 * 60 * 60 * 1000); // 14 dní
    
    createCreditNoteMutation.mutate({
      invoice_id: creditNoteInvoice.id,
      issue_date: issueDate,
      due_date: dueDate,
      reason_type: creditNoteReason,
      reason: creditNoteReasonText,
      notes: creditNoteNotes,
      items: selectedItems,
    });
  };

  const handleMarkAsCancelled = (invoiceId: number) => {
    updateStatusMutation.mutate({
      invoiceId,
      status: 'cancelled'
    });
  };

  // Client creation handlers
  const handleCreateClient = () => {
    // Basic validation with user feedback
    const errors = [];
    if (!newClientData.businessName.trim()) errors.push('Obchodný názov je povinný');
    if (!newClientData.email.trim()) errors.push('Email je povinný');
    if (!newClientData.hourlyRate || parseFloat(newClientData.hourlyRate) <= 0) errors.push('Hodinová sadzba musí byť väčšia ako 0');
    
    if (errors.length > 0) {
      toast({
        title: 'Chýbajúce údaje',
        description: errors.join(', '),
        variant: 'destructive',
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newClientData.email)) {
      toast({
        title: 'Neplatný email',
        description: 'Zadajte platný email vo formáte example@domain.com',
        variant: 'destructive',
      });
      return;
    }

    // Convert hourly rate to number
    const clientData = {
      ...newClientData,
      hourlyRate: parseFloat(newClientData.hourlyRate)
    };

    createClientMutation.mutate(clientData);
  };

  // Handler pre duplikovanie faktúry
  const handleDuplicateInvoice = (invoice: Invoice) => {
    duplicateInvoiceMutation.mutate(invoice.id);
  };

  // Function to open HTML preview in new window
  const handleOpenHtmlPreview = (invoiceId: number) => {
    const htmlUrl = `/api/invoices/${invoiceId}/html`;
    window.open(htmlUrl, '_blank', 'width=900,height=1200,scrollbars=yes,resizable=yes');
  };

  // Mutácia pre duplikovanie faktúry
  const duplicateInvoiceMutation = useMutation({
    mutationFn: async (invoiceId: number) => {
      const response = await apiRequest("POST", `/api/invoices/${invoiceId}/duplicate`);
      return response.json();
    },
    onSuccess: (newInvoice) => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      toast({
        title: "Faktúra duplikovaná",
        description: `Nová faktúra č. ${newInvoice.invoiceNumber} bola úspešne vytvorená`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Informácia",
        description: error.message || "Nepodarilo sa duplikovať faktúru",
        variant: "default",
      });
    },
  });

  const handlePrint = async () => {
    if (!selectedInvoice) return;
    
    setIsPrintingToPdf(true);
    
    try {
      // Otvoríme HTML náhľad v novom okne pre tlač
      const printWindow = window.open(
        `/api/invoices/${selectedInvoice.id}/html?t=${Date.now()}`, 
        '_blank',
        'width=900,height=1200,scrollbars=yes,resizable=yes'
      );
      
      if (!printWindow) {
        throw new Error('Nepodarilo sa otvoriť okno pre tlač');
      }
      
      // Počkáme kým sa obsah načíta a spustíme tlač
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };
    } catch (error) {
      console.error('Print error:', error);
      toast({
        title: 'Chyba',
        description: 'Nastala chyba pri príprave tlače',
        variant: 'destructive',
      });
    } finally {
      setIsPrintingToPdf(false);
    }
  };

  const handleExportPdf = async () => {
    if (!selectedInvoice) return;
    
    setIsPrintingToPdf(true);
    
    try {
      // Používame server-side PDF generovanie
      const invoiceNumber = selectedInvoice.invoiceNumber || selectedInvoice.invoice_number;
      await handleDownloadPdf(selectedInvoice.id, invoiceNumber);
    } catch (error) {
      console.error('PDF export error:', error);
      toast({
        title: 'Chyba',
        description: 'Nastala chyba pri exporte do PDF',
        variant: 'destructive',
      });
    } finally {
      setIsPrintingToPdf(false);
    }
  };
  
  // Funkcia na export všetkých faktúr ako ZIP so samostatnými PDF súbormi
  const handleExportAllInvoices = async () => {
    if (!invoices || invoices.length === 0) {
      toast({
        title: 'Žiadne faktúry',
        description: 'Nie sú k dispozícii žiadne faktúry na export',
        variant: 'destructive',
      });
      return;
    }
    
    toast({
      title: 'Export všetkých faktúr',
      description: 'Pripravujem export všetkých faktúr, prosím čakajte...',
    });
    
    try {
      const response = await fetch('/api/export/invoices', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/zip',
        },
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Nie ste prihlásený');
        }
        const errorData = await response.json().catch(() => ({ error: 'Export sa nepodaril' }));
        throw new Error(errorData.error || 'Export sa nepodaril');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `faktury-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: 'Export dokončený',
        description: `Všetky faktúry boli exportované ako ZIP súbor s ${invoices.length} samostatnými PDF súbormi`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: 'Chyba',
        description: error instanceof Error ? error.message : 'Nastala chyba pri exporte všetkých faktúr',
        variant: 'destructive',
      });
    }
  };

  // Optimized filtering with useMemo
  const filteredInvoices = useMemo(() => {
    console.log('🔍 Filtering invoices:', invoices);
    if (!invoices || !Array.isArray(invoices)) {
      console.log('❌ No invoices to filter');
      return [];
    }
    
    const filtered = invoices.filter((invoice: any) => {
      // Filter by client
      if (filterClientId && filterClientId !== '' && filterClientId !== 'all') {
        if (invoice.clientId !== parseInt(filterClientId)) {
          return false;
        }
      }

      // Filter by date range
      if (filterDateFrom || filterDateTo) {
        const invoiceDate = new Date(invoice.createdAt || invoice.issueDate);
        
        if (filterDateFrom && invoiceDate < filterDateFrom) {
          return false;
        }
        
        if (filterDateTo) {
          // Add one day to include the end date
          const endDate = new Date(filterDateTo);
          endDate.setHours(23, 59, 59, 999);
          if (invoiceDate > endDate) {
            return false;
          }
        }
      }

      return true;
    });
    
    console.log('✅ Filtered invoices:', filtered.length);
    return filtered;
  }, [invoices, filterClientId, filterDateFrom, filterDateTo]);

  // Optimized clear filters function
  const clearFilters = useCallback(() => {
    setFilterClientId('');
    setFilterDateFrom(undefined);
    setFilterDateTo(undefined);
  }, []);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  
  // Calculate pagination
  const totalItems = filteredInvoices.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentInvoices = filteredInvoices.slice(startIndex, endIndex);
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterClientId, filterDateFrom, filterDateTo]);

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="flex justify-center items-center h-96">
          <div className="text-center space-y-4">
            <div className="animate-pulse text-lg">
              {invoiceIdFromUrl ? 'Načítavam faktúru...' : 'Načítavam faktúry...'}
            </div>
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (invoiceIdFromUrl && isLoadingInvoiceFromUrl) {
    return (
      <div className="page-container">
        <div className="flex justify-center items-center h-96">
          <div className="text-center space-y-4">
            <div className="animate-pulse text-lg">Načítavam faktúru...</div>
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="flex flex-col justify-center items-center h-96 space-y-4">
          <div className="text-red-600">Chyba pri načítaní faktúr</div>
          <div className="text-sm text-gray-500">{error.message}</div>
          <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['/api/invoices'] })}>
            Skúsiť znova
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="min-h-screen space-y-8 min-w-0">
        {invoiceNotFoundFromUrl && invoiceIdFromUrl && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="font-medium text-destructive">Faktúra nebola nájdená.</p>
              <p className="text-sm text-muted-foreground mt-1">
                Faktúra s ID {invoiceIdFromUrl} neexistuje alebo nemáte k nej prístup.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setLocation(getLocalizedUrl('/invoices'));
                setInvoiceNotFoundFromUrl(false);
              }}
            >
              Späť na faktúry
            </Button>
          </div>
        )}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 section-spacing">
          <div>
            <h1 className="page-title">{t('nav:invoices')}</h1>
            <p className="text-muted-foreground text-lg">{t('invoices:description')}</p>
          </div>
        <div className="flex flex-col xs:flex-row gap-2">
          <Button variant="outline" onClick={handleExportAllInvoices} title="Stiahnuť všetky faktúry ako PDF" className="w-full xs:w-auto">
            <FileDown className="mr-2 h-4 w-4" />
            <span className="hidden xs:inline">Stiahnuť všetky</span>
            <span className="xs:hidden">Export PDF</span>
          </Button>
          <Button onClick={() => setIsCreateDialogOpen(true)} className="w-full xs:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            <span className="hidden xs:inline">Vytvoriť faktúru</span>
            <span className="xs:hidden">Nová faktúra</span>
          </Button>
        </div>
        </div>

        {/* Fixed Visible Filters */}
        <UnifiedFilter
          showClient={true}
          showDateRange={true}
          clientId={filterClientId || undefined}
          dateFrom={filterDateFrom}
          dateTo={filterDateTo}
          onClientChange={(value) => setFilterClientId(value === 'all' ? '' : value)}
          onDateFromChange={setFilterDateFrom}
          onDateToChange={setFilterDateTo}
          onClear={clearFilters}
          clients={clients || []}
          resultCount={filteredInvoices.length}
          totalCount={invoices?.length || 0}
          t={(key) => t(`invoices:${key}`) || key}
        />




        <Tabs defaultValue="all" className="mt-8">
          <TabsList className="mb-4 w-full justify-start overflow-x-auto">
            <TabsTrigger value="all" className="whitespace-nowrap">Všetky ({filteredInvoices.length})</TabsTrigger>
            <TabsTrigger value="draft" className="whitespace-nowrap">Koncepty</TabsTrigger>
            <TabsTrigger value="sent" className="whitespace-nowrap">Odoslané</TabsTrigger>
            <TabsTrigger value="paid" className="whitespace-nowrap">Zaplatené</TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            <InvoiceTable 
              invoices={currentInvoices} 
              clients={clients || []}
              userData={userData}
              onPreview={handlePreviewClick}
              onDelete={handleDeleteClick}
              onEmail={handleEmailClick}
              onEdit={handleEditClick}
              onInlineEdit={handleInlineEdit}
              onMarkAsPaid={handleMarkAsPaid}
              onMarkAsCancelled={handleMarkAsCancelled}
              onCreateCreditNote={handleCreateCreditNote}
              onDuplicate={handleDuplicateInvoice}
            />
          </TabsContent>

          <TabsContent value="draft">
            <InvoiceTable 
              invoices={filteredInvoices.filter((inv: any) => inv.status === 'draft').slice(startIndex, endIndex)} 
              clients={clients || []}
              userData={userData}
              onPreview={handlePreviewClick}
              onDelete={handleDeleteClick}
              onEmail={handleEmailClick}
              onEdit={handleEditClick}
              onInlineEdit={handleInlineEdit}
              onMarkAsPaid={handleMarkAsPaid}
              onMarkAsCancelled={handleMarkAsCancelled}
              onCreateCreditNote={handleCreateCreditNote}
              onDuplicate={handleDuplicateInvoice}
            />
          </TabsContent>

          <TabsContent value="sent">
            <InvoiceTable 
              invoices={filteredInvoices.filter((inv: any) => inv.status === 'sent').slice(startIndex, endIndex)} 
              clients={clients || []}
              userData={userData}
              onPreview={handlePreviewClick}
              onDelete={handleDeleteClick}
              onEmail={handleEmailClick}
              onEdit={handleEditClick}
              onInlineEdit={handleInlineEdit}
              onMarkAsPaid={handleMarkAsPaid}
              onMarkAsCancelled={handleMarkAsCancelled}
              onCreateCreditNote={handleCreateCreditNote}
              onDuplicate={handleDuplicateInvoice}
            />
          </TabsContent>

          <TabsContent value="paid">
            <InvoiceTable 
              invoices={filteredInvoices.filter((inv: any) => inv.status === 'paid').slice(startIndex, endIndex)} 
              clients={clients || []}
              userData={userData}
              onPreview={handlePreviewClick}
              onDelete={handleDeleteClick}
              onEmail={handleEmailClick}
              onEdit={handleEditClick}
              onInlineEdit={handleInlineEdit}
              onMarkAsPaid={handleMarkAsPaid}
              onMarkAsCancelled={handleMarkAsCancelled}
              onCreateCreditNote={handleCreateCreditNote}
              onDuplicate={handleDuplicateInvoice}
            />
          </TabsContent>
        </Tabs>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-8 p-4 bg-card rounded-lg border">
            <div className="text-sm text-muted-foreground">
              Stránka {currentPage} z {totalPages} ({totalItems} faktúr celkom)
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                Prvá
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Predošlá
              </Button>
              <span className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded">
                {currentPage}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Ďalšia
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Posledná
              </Button>
            </div>
          </div>
        )}

        {/* Show total invoices message */}
        {invoices && invoices.length > 0 && (
          <div className="text-center py-4 text-muted-foreground">
            Celkom faktúr: {invoices.length}
          </div>
        )}

        {/* Create Invoice Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-background/80 backdrop-blur-xl shadow-2xl border-l-4 border-l-primary/30">
            <DialogHeader>
              <DialogTitle>Vytvoriť novú faktúru</DialogTitle>
              <DialogDescription>
                Vyberte klienta a úlohy, ktoré chcete zahrnúť do faktúry.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 my-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-foreground">Klient</label>
                <select 
                  value={selectedClientId?.toString() || ''} 
                  onChange={(e) => handleClientChange(e.target.value)}
                  className="flex h-12 w-full rounded-xl border border-input bg-background/90 backdrop-blur-md px-4 py-3 text-sm font-medium ring-offset-background placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2 focus-visible:border-primary focus-visible:bg-background focus-visible:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300 hover:border-primary/40 hover:bg-background/95 hover:shadow-md"
                >
                  <option value="">Vyberte klienta</option>
                  {Array.isArray(clients) && clients.length > 0 ? clients.map(client => (
                    <option key={client.id} value={client.id.toString()}>
                      {client.businessName || client.name}
                    </option>
                  )) : (
                    <option value="" disabled>Žiadni klienti</option>
                  )}
                </select>
              </div>

              {selectedClientId && (
                <div className="space-y-6">
                  {/* Fakturačné údaje */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h3 className="font-medium text-base mb-3">Dátumy</h3>
                      <div className="grid grid-cols-1 gap-3">
                        <div>
                          <label className="block text-sm font-medium mb-1 text-foreground">Vystavenie</label>
                          <Input 
                            type="date" 
                            value={issueDateValue}
                            onChange={(e) => setIssueDateValue(e.target.value)}
                            className="bg-background border-input text-foreground"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1 text-foreground">Dodanie</label>
                          <Input 
                            type="date" 
                            value={deliveryDateValue}
                            onChange={(e) => setDeliveryDateValue(e.target.value)}
                            className="bg-background border-input text-foreground"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1 text-foreground">Splatnosť</label>
                          <Input 
                            type="date" 
                            value={dueDateValue}
                            onChange={(e) => setDueDateValue(e.target.value)}
                            className="bg-background border-input text-foreground"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-medium text-base mb-3">Moje platobné údaje</h3>
                      <div className="grid grid-cols-1 gap-3">
                        <div>
                          <label className="block text-sm font-medium mb-1 text-foreground">Spôsob úhrady</label>
                          <select 
                            value={paymentMethodValue} 
                            onChange={(e) => setPaymentMethodValue(e.target.value)}
                            className="flex h-12 w-full rounded-xl border border-input bg-background/90 backdrop-blur-md px-4 py-3 text-sm font-medium ring-offset-background placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2 focus-visible:border-primary focus-visible:bg-background focus-visible:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300 hover:border-primary/40 hover:bg-background/95 hover:shadow-md"
                          >
                            <option value="">Vyberte spôsob úhrady</option>
                            <option value="prevod">Prevodom</option>
                            <option value="karta">Kartou</option>
                            <option value="hotovost">V hotovosti</option>
                            <option value="dobierka">Dobierkou</option>
                            <option value="zapoctom">Zápočtom</option>
                            <option value="zlozenka">Zloženkou</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-medium text-base mb-3">Položky faktúry</h3>
                    <div>
                      <label className="block text-sm font-medium mb-1">Úlohy</label>
                      <div className="border border-border/30 rounded p-2 max-h-60 overflow-y-auto overflow-x-auto bg-background/30 min-w-0">
                        {(nonInvoicedTasks?.length || 0) === 0 ? (
                          <p className="text-sm text-muted-foreground p-2">
                            Tento klient nemá žiadne nevyfakturované úlohy.
                          </p>
                        ) : (
                          <div className="w-full overflow-x-auto min-w-0">
                          <Table className="min-w-[560px]">
                            <TableHeader>
                              <TableRow className="bg-muted/10">
                                <TableHead className="w-10"></TableHead>
                                <TableHead className="text-foreground/80">Názov</TableHead>
                                <TableHead className="text-foreground/80">Hodiny</TableHead>
                                <TableHead className="text-foreground/80">Stav</TableHead>
                                <TableHead className="text-right text-foreground/80">Suma</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody className="bg-background/50">
                              {Array.isArray(nonInvoicedTasks) ? nonInvoicedTasks.map(task => {
                                const client = Array.isArray(clients) ? clients.find(c => c.id === task.clientId) : undefined;
                                const rate = client ? Number(client.hourlyRate) : 0;
                                const amount = parseFloat(task.hoursWorked) * rate;
                                
                                return (
                                  <TableRow key={task.id}>
                                    <TableCell>
                                      <input 
                                        type="checkbox" 
                                        checked={selectedTaskIds.includes(task.id)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedTaskIds(prev => [...prev, task.id]);
                                          } else {
                                            setSelectedTaskIds(prev => 
                                              prev.filter(id => id !== task.id)
                                            );
                                          }
                                        }}
                                        className="w-4 h-4"
                                      />
                                    </TableCell>
                                    <TableCell>{task.title}</TableCell>
                                    <TableCell>{task.hoursWorked ? `${task.hoursWorked}h` : '-'}</TableCell>
                                    <TableCell>
                                      {task.status === 'completed' ? 'Dokončená' : 
                                      task.status === 'in_progress' ? 'Rozpracovaná' : 'Čaká'}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {formatCurrency(amount)}
                                    </TableCell>
                                  </TableRow>
                                );
                              }) : null}
                            </TableBody>
                          </Table>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Custom invoice items section */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium">Vlastné položky</label>
                      <Button 
                        variant="outline" 
                        size="sm"
                        type="button"
                        className="text-xs h-7"
                        onClick={() => {
                          // Otvorenie dialógu pre pridanie položky
                          const uniqueId = Date.now().toString();
                          const newItem = {
                            id: uniqueId,
                            name: '',
                            price: 0,
                            quantity: 1,
                            unit: 'ks',
                            vatRate: userData?.isVatPayer ? (userData?.companyVatRate || 20) : 0,
                            total: 0
                          };
                          setCurrentItem(newItem);
                          
                          // Pridanie položky do zoznamu
                          setInvoiceItems([...invoiceItems, newItem]);
                        }}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Pridať položku
                      </Button>
                    </div>
                    
                    {invoiceItems.length === 0 ? (
                      <div className="border border-border/30 rounded p-2 flex items-center justify-center h-20 bg-background/30">
                        <p className="text-sm text-muted-foreground">
                          Pridajte vlastné položky bez prepojenia s úlohami
                        </p>
                      </div>
                    ) : (
                      <div className="w-full overflow-x-auto min-w-0 border border-border/30 rounded bg-background/20">
                        <table className="min-w-[700px] w-full divide-y divide-border/30">
                          <thead className="bg-muted/20">
                            <tr>
                              <th className="px-2 py-2 text-left text-xs font-medium text-foreground/80 uppercase tracking-wider">Položka</th>
                              <th className="px-2 py-2 text-right text-xs font-medium text-foreground/80 uppercase tracking-wider">Cena</th>
                              <th className="px-2 py-2 text-right text-xs font-medium text-foreground/80 uppercase tracking-wider">Množstvo</th>
                              <th className="px-2 py-2 text-left text-xs font-medium text-foreground/80 uppercase tracking-wider">Jednotka</th>
                              <th className="px-2 py-2 text-right text-xs font-medium text-foreground/80 uppercase tracking-wider">DPH (%)</th>
                              <th className="px-2 py-2 text-right text-xs font-medium text-foreground/80 uppercase tracking-wider">Spolu</th>
                              <th className="px-2 py-2 text-right text-xs font-medium text-foreground/80 uppercase tracking-wider">Akcie</th>
                            </tr>
                          </thead>
                          <tbody className="bg-background/50 divide-y divide-border/50">
                            {Array.isArray(invoiceItems) ? invoiceItems.map((item, index) => (
                              <tr key={item.id}>
                                <td className="px-2 py-2 whitespace-nowrap">
                                  <Input 
                                    value={item.name} 
                                    className="w-full text-sm"
                                    onChange={(e) => {
                                      const updatedItems = [...invoiceItems];
                                      updatedItems[index].name = e.target.value;
                                      setInvoiceItems(updatedItems);
                                    }}
                                  />
                                </td>
                                <td className="px-2 py-2 whitespace-nowrap">
                                  <Input 
                                    type="number" 
                                    value={item.price} 
                                    className="w-full text-sm text-right"
                                    onChange={(e) => {
                                      const updatedItems = [...invoiceItems];
                                      const price = parseFloat(e.target.value) || 0;
                                      updatedItems[index].price = price;
                                      
                                      // Prepočítať celkovú sumu
                                      updatedItems[index].total = price * updatedItems[index].quantity;
                                      
                                      setInvoiceItems(updatedItems);
                                    }}
                                  />
                                </td>
                                <td className="px-2 py-2 whitespace-nowrap">
                                  <Input 
                                    type="number" 
                                    value={item.quantity} 
                                    className="w-full text-sm text-right"
                                    onChange={(e) => {
                                      const updatedItems = [...invoiceItems];
                                      const quantity = parseFloat(e.target.value) || 0;
                                      updatedItems[index].quantity = quantity;
                                      
                                      // Prepočítať celkovú sumu
                                      updatedItems[index].total = updatedItems[index].price * quantity;
                                      
                                      setInvoiceItems(updatedItems);
                                    }}
                                  />
                                </td>
                                <td className="px-2 py-2 whitespace-nowrap">
                                  <Input 
                                    value={item.unit} 
                                    className="w-full text-sm"
                                    onChange={(e) => {
                                      const updatedItems = [...invoiceItems];
                                      updatedItems[index].unit = e.target.value;
                                      setInvoiceItems(updatedItems);
                                    }}
                                  />
                                </td>
                                <td className="px-2 py-2 whitespace-nowrap">
                                  <select 
                                    value={item.vatRate} 
                                    className="w-full text-sm text-right border border-input bg-background text-foreground rounded px-2 py-1"
                                    onChange={(e) => {
                                      const updatedItems = [...invoiceItems];
                                      updatedItems[index].vatRate = parseInt(e.target.value);
                                      setInvoiceItems(updatedItems);
                                    }}
                                  >
                                    <option value="0">0%</option>
                                    <option value="10">10%</option>
                                    <option value="20">20%</option>
                                  </select>
                                </td>
                                <td className="px-2 py-2 whitespace-nowrap text-right text-sm font-medium">
                                  {formatCurrency(item.total)}
                                </td>
                                <td className="px-2 py-2 whitespace-nowrap text-right text-sm font-medium">
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => {
                                      const updatedItems = invoiceItems.filter((_, i) => i !== index);
                                      setInvoiceItems(updatedItems);
                                    }}
                                  >
                                    <MinusCircle className="h-3 w-3 text-red-500" />
                                  </Button>
                                </td>
                              </tr>
                            )) : null}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Zrušiť
              </Button>
              <Button 
                onClick={handleCreateInvoice}
                disabled={!selectedClientId || (selectedTaskIds.length === 0 && invoiceItems.length === 0) || createInvoiceMutation.isPending}
              >
                {createInvoiceMutation.isPending ? 'Spracovanie...' : 'Vytvoriť faktúru'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Edit Invoice Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-background/80 backdrop-blur-xl shadow-2xl border-l-4 border-l-primary/30">
            <DialogHeader>
              <DialogTitle>Upraviť faktúru {selectedInvoice?.invoiceNumber}</DialogTitle>
              <DialogDescription>
                Upravte úlohy, ktoré chcete zahrnúť do faktúry.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 my-4">
              <div>
                <label className="block text-sm font-medium mb-1">Klient</label>
                <div className="p-2 border rounded bg-gray-50">
                  {Array.isArray(clients) ? (clients.find(c => c.id === selectedClientId)?.businessName || 
                   clients.find(c => c.id === selectedClientId)?.name || 
                   'Neznámy klient') : 'Neznámy klient'}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Aktuálne úlohy faktúry</label>
                  <div className="border rounded p-2 max-h-60 overflow-y-auto overflow-x-auto min-w-0">
                    {(invoiceTasks?.length || 0) === 0 ? (
                      <p className="text-sm text-muted-foreground p-2">
                        Táto faktúra nemá priradené žiadne úlohy.
                      </p>
                    ) : (
                      <div className="w-full overflow-x-auto min-w-0">
                      <Table className="min-w-[560px]">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10"></TableHead>
                            <TableHead>Názov</TableHead>
                            <TableHead>Hodiny</TableHead>
                            <TableHead>Stav</TableHead>
                            <TableHead className="text-right">Suma</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Array.isArray(invoiceTasks) ? invoiceTasks.map(task => {
                            const client = Array.isArray(clients) ? clients.find(c => c.id === task.clientId) : undefined;
                            let amount = 0;
                            let hoursDisplay = '';
                            
                            if (task.type === "cash" || task.type === "settlement") {
                              // Pre hotovosť a zápočet použijeme manuálnu sumu
                              amount = task.manualAmount ? parseFloat(task.manualAmount.toString()) : 0;
                              // Pre zápočty môže byť suma aj záporná
                              // Pre zápočty už máme záporné sumy v databáze, takže ich používame tak ako sú
                              hoursDisplay = '1 ks';
                            } else {
                              // Pre štandardné úlohy a bonusy použijeme štandardný výpočet
                              const rate = client ? Number(client.hourlyRate) : 0;
                              const hoursValue = task.hoursWorked ? parseFloat(task.hoursWorked.toString()) : 0;
                              amount = hoursValue * rate;
                              hoursDisplay = hoursValue > 0 ? `${hoursValue}h` : '-';
                            }
                            
                            // Definícia štítkov pre rôzne typy úloh
                            const typeLabels = {
                              standard: "",
                              cash: "⚪ Hotovosť",
                              offset: "🔄 Zápočet",
                              bonus: "⭐ Bonus"
                            };
                            
                            // Získanie označenia pre typ úlohy (prázdne pre štandardné)
                            const typeLabel = task.type && task.type !== "standard" 
                              ? typeLabels[task.type as keyof typeof typeLabels]
                              : "";
                            
                            const taskRows = [];
                            
                            // Hlavný riadok úlohy
                            taskRows.push(
                              <TableRow key={task.id}>
                                <TableCell>
                                  <input 
                                    type="checkbox" 
                                    checked={selectedTaskIds.includes(task.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedTaskIds(prev => [...prev, task.id]);
                                      } else {
                                        setSelectedTaskIds(prev => 
                                          prev.filter(id => id !== task.id)
                                        );
                                      }
                                    }}
                                    className="w-4 h-4"
                                  />
                                </TableCell>
                                <TableCell>
                                  {typeLabel && <span className="mr-1 text-xs">{typeLabel}</span>}
                                  {task.title}
                                </TableCell>
                                <TableCell>{hoursDisplay || '-'}</TableCell>
                                <TableCell>
                                  {task.status === 'completed' ? 'Dokončená' : 
                                  task.status === 'in_progress' ? 'Rozpracovaná' : 'Čaká'}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(amount)}
                                </TableCell>
                              </TableRow>
                            );
                            
                            // Pridáme položky úlohy ak existujú
                            if ((task as any).items && (task as any).items.length > 0) {
                              (task as any).items.forEach((item: any) => {
                                taskRows.push(
                                  <TableRow key={`item-${item.id}`} className="bg-muted/50">
                                    <TableCell></TableCell>
                                    <TableCell className="text-sm text-muted-foreground pl-6">
                                      • {item.name}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                      {item.quantity}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                      {formatCurrency(parseFloat(item.unitPrice))}
                                    </TableCell>
                                    <TableCell className="text-right text-sm text-muted-foreground">
                                      {formatCurrency(parseFloat(item.amount))}
                                    </TableCell>
                                  </TableRow>
                                );
                              });
                            }
                            
                            return taskRows;
                          }).flat() : null}
                        </TableBody>
                      </Table>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Dostupné ďalšie úlohy</label>
                  <div className="border rounded p-2 max-h-60 overflow-y-auto overflow-x-auto min-w-0">
                    {(nonInvoicedTasks?.length || 0) === 0 ? (
                      <p className="text-sm text-muted-foreground p-2">
                        Tento klient nemá žiadne nevyfakturované úlohy.
                      </p>
                    ) : (
                      <div className="w-full overflow-x-auto min-w-0">
                      <Table className="min-w-[560px]">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10"></TableHead>
                            <TableHead>Názov</TableHead>
                            <TableHead>Hodiny</TableHead>
                            <TableHead>Stav</TableHead>
                            <TableHead className="text-right">Suma</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Array.isArray(nonInvoicedTasks) ? nonInvoicedTasks.map(task => {
                            const client = Array.isArray(clients) ? clients.find(c => c.id === task.clientId) : undefined;
                            let amount = 0;
                            let hoursDisplay = '';
                            
                            if (task.type === "cash" || task.type === "settlement") {
                              // Pre hotovosť a zápočet použijeme manuálnu sumu
                              amount = task.manualAmount ? parseFloat(task.manualAmount.toString()) : 0;
                              // Pre zápočty môže byť suma aj záporná
                              // Pre zápočty už máme záporné sumy v databáze, takže ich používame tak ako sú
                              hoursDisplay = '1 ks';
                            } else {
                              // Pre štandardné úlohy a bonusy použijeme štandardný výpočet
                              const rate = client ? Number(client.hourlyRate) : 0;
                              const hoursValue = task.hoursWorked ? parseFloat(task.hoursWorked.toString()) : 0;
                              amount = hoursValue * rate;
                              hoursDisplay = hoursValue > 0 ? `${hoursValue}h` : '-';
                            }
                            
                            // Definícia štítkov pre rôzne typy úloh
                            const typeLabels = {
                              standard: "",
                              cash: "⚪ Hotovosť",
                              offset: "🔄 Zápočet",
                              bonus: "⭐ Bonus"
                            };
                            
                            // Získanie označenia pre typ úlohy (prázdne pre štandardné)
                            const typeLabel = task.type && task.type !== "standard" 
                              ? typeLabels[task.type as keyof typeof typeLabels]
                              : "";
                            
                            const taskRows = [];
                            
                            // Hlavný riadok úlohy
                            taskRows.push(
                              <TableRow key={task.id}>
                                <TableCell>
                                  <input 
                                    type="checkbox" 
                                    checked={selectedTaskIds.includes(task.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedTaskIds(prev => [...prev, task.id]);
                                      } else {
                                        setSelectedTaskIds(prev => 
                                          prev.filter(id => id !== task.id)
                                        );
                                      }
                                    }}
                                    className="w-4 h-4"
                                  />
                                </TableCell>
                                <TableCell>
                                  {typeLabel && <span className="mr-1 text-xs">{typeLabel}</span>}
                                  {task.title}
                                </TableCell>
                                <TableCell>{hoursDisplay || '-'}</TableCell>
                                <TableCell>
                                  {task.status === 'completed' ? 'Dokončená' : 
                                  task.status === 'in_progress' ? 'Rozpracovaná' : 'Čaká'}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(amount)}
                                </TableCell>
                              </TableRow>
                            );
                            
                            // Pridáme položky úlohy ak existujú
                            if ((task as any).items && (task as any).items.length > 0) {
                              (task as any).items.forEach((item: any) => {
                                taskRows.push(
                                  <TableRow key={`item-${item.id}`} className="bg-muted/50">
                                    <TableCell></TableCell>
                                    <TableCell className="text-sm text-muted-foreground pl-6">
                                      • {item.name}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                      {item.quantity}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                      {formatCurrency(parseFloat(item.unitPrice))}
                                    </TableCell>
                                    <TableCell className="text-right text-sm text-muted-foreground">
                                      {formatCurrency(parseFloat(item.amount))}
                                    </TableCell>
                                  </TableRow>
                                );
                              });
                            }
                            
                            return taskRows;
                          }).flat() : null}
                        </TableBody>
                      </Table>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Custom invoice items section */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium">Vlastné položky</label>
                    <Button 
                      variant="outline" 
                      size="sm"
                      type="button"
                      className="text-xs h-7"
                      onClick={() => {
                        // Otvorenie dialógu pre pridanie položky
                        const uniqueId = Date.now().toString();
                        const newItem = {
                          id: uniqueId,
                          name: '',
                          price: 0,
                          quantity: 1,
                          unit: 'ks',
                          vatRate: 20,
                          total: 0
                        };
                        setCurrentItem(newItem);
                        
                        // Pridanie položky do zoznamu
                        setInvoiceItems([...invoiceItems, newItem]);
                      }}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Pridať položku
                    </Button>
                  </div>
                  
                  {invoiceItems.length === 0 ? (
                    <div className="border rounded p-2 flex items-center justify-center h-20">
                      <p className="text-sm text-gray-500">
                        Pridajte vlastné položky bez prepojenia s úlohami
                      </p>
                    </div>
                  ) : (
                    <div className="w-full overflow-x-auto min-w-0 border rounded">
                      <table className="min-w-[700px] w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Položka</th>
                            <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cena</th>
                            <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Množstvo</th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jednotka</th>
                            <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">DPH (%)</th>
                            <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Spolu</th>
                            <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Akcie</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {Array.isArray(invoiceItems) ? invoiceItems.map((item, index) => (
                            <tr key={item.id}>
                              <td className="px-2 py-2 whitespace-nowrap">
                                <Input 
                                  value={item.name} 
                                  className="w-full text-sm"
                                  onChange={(e) => {
                                    const updatedItems = [...invoiceItems];
                                    updatedItems[index].name = e.target.value;
                                    setInvoiceItems(updatedItems);
                                  }}
                                />
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap">
                                <Input 
                                  type="number" 
                                  value={item.price} 
                                  className="w-full text-sm text-right"
                                  onChange={(e) => {
                                    const updatedItems = [...invoiceItems];
                                    const price = parseFloat(e.target.value) || 0;
                                    updatedItems[index].price = price;
                                    
                                    // Prepočítať celkovú sumu
                                    updatedItems[index].total = price * updatedItems[index].quantity;
                                    
                                    setInvoiceItems(updatedItems);
                                  }}
                                />
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap">
                                <Input 
                                  type="number" 
                                  value={item.quantity} 
                                  className="w-full text-sm text-right"
                                  onChange={(e) => {
                                    const updatedItems = [...invoiceItems];
                                    const quantity = parseFloat(e.target.value) || 0;
                                    updatedItems[index].quantity = quantity;
                                    
                                    // Prepočítať celkovú sumu
                                    updatedItems[index].total = updatedItems[index].price * quantity;
                                    
                                    setInvoiceItems(updatedItems);
                                  }}
                                />
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap">
                                <Input 
                                  value={item.unit} 
                                  className="w-full text-sm"
                                  onChange={(e) => {
                                    const updatedItems = [...invoiceItems];
                                    updatedItems[index].unit = e.target.value;
                                    setInvoiceItems(updatedItems);
                                  }}
                                />
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap">
                                <select 
                                  value={item.vatRate} 
                                  className="w-full text-sm text-right border border-input bg-background text-foreground rounded px-2 py-1"
                                  onChange={(e) => {
                                    const updatedItems = [...invoiceItems];
                                    updatedItems[index].vatRate = parseInt(e.target.value);
                                    setInvoiceItems(updatedItems);
                                  }}
                                >
                                  <option value="0">0%</option>
                                  <option value="10">10%</option>
                                  <option value="20">20%</option>
                                </select>
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap text-right text-sm font-medium">
                                {formatCurrency(item.total)}
                              </td>
                              <td className="px-2 py-2 whitespace-nowrap text-right text-sm font-medium">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => {
                                    const updatedItems = invoiceItems.filter((_, i) => i !== index);
                                    setInvoiceItems(updatedItems);
                                  }}
                                >
                                  <MinusCircle className="h-3 w-3 text-red-500" />
                                </Button>
                              </td>
                            </tr>
                          )) : null}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Zrušiť
              </Button>
              <Button 
                onClick={handleUpdateInvoice}
                disabled={!selectedInvoice || !selectedClientId || selectedTaskIds.length === 0 || updateInvoiceMutation.isPending}
              >
                {updateInvoiceMutation.isPending ? 'Spracovanie...' : 'Uložiť zmeny'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Delete Invoice Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Vymazať faktúru?</AlertDialogTitle>
              <AlertDialogDescription>
                Naozaj chcete vymazať faktúru {selectedInvoice?.invoiceNumber}? Táto akcia nemôže byť vrátená.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Zrušiť</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (selectedInvoice) {
                    deleteMutation.mutate(selectedInvoice.id);
                  }
                }}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleteMutation.isPending ? 'Vymazávanie...' : 'Vymazať'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
        {/* Email Invoice Dialog */}
        <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
          <DialogContent className="max-w-md bg-background/80 backdrop-blur-xl shadow-2xl border-l-4 border-l-primary/30">
            <DialogHeader>
              <DialogTitle>Odoslať faktúru emailom</DialogTitle>
              <DialogDescription>
                Zadajte emailovú adresu, na ktorú odošleme faktúru {selectedInvoice?.invoiceNumber}.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 my-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input 
                  type="email"
                  className="w-full p-2 border rounded"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder="klient@example.com"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEmailDialogOpen(false)}>
                Zrušiť
              </Button>
              <Button 
                onClick={() => selectedInvoice && handleEmailSend(selectedInvoice.id)}
                disabled={!emailAddress || updateStatusMutation.isPending}
              >
                {updateStatusMutation.isPending ? 'Odosielanie...' : 'Odoslať faktúru'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Invoice Dialog */}
        <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
          <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto bg-white shadow-lg border border-gray-200 rounded-lg p-0" style={{ backgroundColor: 'white', color: 'black' }} aria-describedby="invoice-preview-desc">
            <DialogHeader className="flex flex-row items-center justify-between px-6 py-4 border-b border-gray-200 print:hidden" style={{ backgroundColor: 'white' }}>
              <DialogTitle className="text-lg font-semibold text-gray-800" style={{ color: '#1f2937' }}>Faktúra {selectedInvoice?.invoiceNumber}</DialogTitle>
              <DialogDescription id="invoice-preview-desc" className="sr-only">
                Náhľad faktúry. Exportujte ako PDF alebo vytlačte.
              </DialogDescription>
              <div className="flex gap-2 items-center">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => selectedInvoice && window.open(`/api/invoices/${selectedInvoice.id}/html?t=${Date.now()}`, '_blank')}
                  title="HTML náhľad v novom okne"
                  className="bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200 shadow-sm"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  HTML náhľad
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handlePrint}
                  disabled={isPrintingToPdf}
                  title="Tlačiť faktúru"
                  className="bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200 shadow-sm"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Tlačiť
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleExportPdf}
                  disabled={isPrintingToPdf}
                  title="Stiahnuť ako PDF"
                  className="bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200 shadow-sm"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Stiahnuť PDF
                </Button>
              </div>
            </DialogHeader>
            
            {selectedInvoice && (
              <div className="w-full">
                {/* Credit Notes Section */}
                {invoiceCreditNotes && invoiceCreditNotes.length > 0 && (
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 dark:bg-gray-900/50">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Dobropisy k tejto faktúre</h3>
                    <div className="space-y-2">
                      {invoiceCreditNotes.map((creditNote: any) => (
                        <div key={creditNote.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                              {creditNote.credit_note_number || creditNote.creditNoteNumber || `DN-${creditNote.id}`}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {creditNote.issue_date ? new Date(creditNote.issue_date).toLocaleDateString('sk-SK') : '-'} • {formatCurrency(creditNote.total_amount || creditNote.totalAmount || 0)}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(`/api/dobropisy/${creditNote.id}/pdf`, '_blank')}
                            className="ml-3 shrink-0 h-8 px-3 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <Download className="h-3.5 w-3.5 mr-1.5" />
                            <span className="text-xs">PDF</span>
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Invoice Preview */}
                <div className="w-full h-[calc(90vh-180px)] bg-white overflow-auto p-4">
                  <iframe
                    ref={invoicePreviewRef}
                    id="invoice-preview"
                    src={`/api/invoices/${selectedInvoice.id}/html?t=${Date.now()}`}
                    className="w-full h-full border-0 bg-white rounded"
                    title={`Faktúra ${selectedInvoice.invoiceNumber}`}
                    aria-describedby="invoice-preview-desc"
                  />
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Clients Page Dialog */}
        <Dialog open={isClientPageOpen} onOpenChange={setIsClientPageOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-background/80 backdrop-blur-xl shadow-2xl border-l-4 border-l-primary/30">
            <DialogHeader>
              <DialogTitle>Klienti</DialogTitle>
            </DialogHeader>
            <ClientsPage />
          </DialogContent>
        </Dialog>

        {/* Create Client Dialog */}
        <Dialog open={isCreateClientDialogOpen} onOpenChange={setIsCreateClientDialogOpen}>
          <DialogContent className="bg-background/80 backdrop-blur-xl shadow-2xl border-l-4 border-l-primary/30">
            <DialogHeader>
              <DialogTitle>Nový klient</DialogTitle>
              <DialogDescription>
                Pridajte nového klienta do systému.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 my-4">
              <div>
                <label className="block text-sm font-medium mb-1">Obchodný názov *</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={newClientData.businessName}
                  onChange={(e) => setNewClientData({...newClientData, businessName: e.target.value})}
                  placeholder="Názov spoločnosti"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Kontaktná osoba</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={newClientData.name}
                  onChange={(e) => setNewClientData({...newClientData, name: e.target.value})}
                  placeholder="Meno kontaktnej osoby"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  className="w-full p-2 border rounded"
                  value={newClientData.email}
                  onChange={(e) => setNewClientData({...newClientData, email: e.target.value})}
                  placeholder="email@example.com"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">IČO</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded"
                    value={newClientData.businessId}
                    onChange={(e) => setNewClientData({...newClientData, businessId: e.target.value})}
                    placeholder="12345678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">DIČ</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded"
                    value={newClientData.taxId}
                    onChange={(e) => setNewClientData({...newClientData, taxId: e.target.value})}
                    placeholder="1234567890"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">IČ DPH</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={newClientData.vatId}
                  onChange={(e) => setNewClientData({...newClientData, vatId: e.target.value})}
                  placeholder="SK1234567890"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Adresa</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={newClientData.street}
                  onChange={(e) => setNewClientData({...newClientData, street: e.target.value})}
                  placeholder="Ulica 123"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Mesto</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded"
                    value={newClientData.city}
                    onChange={(e) => setNewClientData({...newClientData, city: e.target.value})}
                    placeholder="Bratislava"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">PSČ</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded"
                    value={newClientData.postalCode}
                    onChange={(e) => setNewClientData({...newClientData, postalCode: e.target.value})}
                    placeholder="84105"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Hodinová sadzba *</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full p-2 border rounded"
                  value={newClientData.hourlyRate}
                  onChange={(e) => setNewClientData({...newClientData, hourlyRate: e.target.value})}
                  placeholder="25.00"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateClientDialogOpen(false)}>
                Zrušiť
              </Button>
              <Button 
                onClick={handleCreateClient}
                disabled={!newClientData.businessName || !newClientData.email || !newClientData.hourlyRate || createClientMutation.isPending}
              >
                {createClientMutation.isPending ? 'Vytváram...' : 'Vytvoriť klienta'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Credit Note Creation Dialog */}
        <Dialog open={isCreditNoteDialogOpen} onOpenChange={setIsCreditNoteDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Vytvoriť dobropis</DialogTitle>
              <DialogDescription>
                Vyberte položky a množstvo, ktoré chcete dobropisovať z faktúry {creditNoteInvoice?.invoiceNumber || creditNoteInvoice?.invoice_number}
              </DialogDescription>
            </DialogHeader>
            
            {creditNoteInvoice && (
              <div className="space-y-4">
                {/* Invoice Info */}
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm font-semibold">Faktúra: {creditNoteInvoice.invoiceNumber || creditNoteInvoice.invoice_number}</p>
                  <p className="text-xs text-muted-foreground">
                    Dátum vystavenia: {creditNoteInvoice.issueDate ? new Date(creditNoteInvoice.issueDate).toLocaleDateString('sk-SK') : '-'}
                  </p>
                </div>
                
                {/* Items Selection */}
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">Položky faktúry</h3>
                  {creditNoteItems.length === 0 ? (
                    <div className="border rounded-lg p-8 text-center">
                      <p className="text-muted-foreground">Faktúra nemá žiadne položky na dobropis</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Skontrolujte, či faktúra obsahuje položky v sekcii display_items
                      </p>
                    </div>
                  ) : (
                    <div className="w-full overflow-x-auto min-w-0 border rounded-lg">
                      <Table className="min-w-[700px]">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">
                              <input
                                type="checkbox"
                                checked={creditNoteItems.every((item: any) => !item.selected || item.credit_quantity === 0)}
                                onChange={(e) => {
                                  // Toto sa použije na "vybrať všetko", ale necháme to prázdne
                                }}
                                className="hidden"
                              />
                            </TableHead>
                            <TableHead>Názov</TableHead>
                            <TableHead className="text-right">Pôvodné množstvo</TableHead>
                            <TableHead className="text-right">Cena za jednotku</TableHead>
                            <TableHead className="text-right">DPH %</TableHead>
                            <TableHead className="text-right">Dobropisované množstvo</TableHead>
                            <TableHead className="text-right">Suma</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {creditNoteItems.map((item: any, index: number) => {
                            const handleQuantityChange = (value: number) => {
                              const newItems = [...creditNoteItems];
                              const maxQty = item.original_quantity;
                              const newQty = Math.max(0, Math.min(value, maxQty));
                              newItems[index] = {
                                ...item,
                                selected: newQty > 0,
                                credit_quantity: newQty,
                                quantity: newQty,
                                amount: newQty * item.unit_price,
                                vat_amount: user?.isVatPayer === true ? (newQty * item.unit_price) * (item.vat_rate / 100) : 0,
                                total_amount: (newQty * item.unit_price) + (user?.isVatPayer === true ? (newQty * item.unit_price) * (item.vat_rate / 100) : 0),
                              };
                              setCreditNoteItems(newItems);
                            };
                            
                            return (
                              <TableRow key={item.id}>
                                <TableCell>
                                  <input
                                    type="checkbox"
                                    checked={item.selected && item.credit_quantity > 0}
                                    onChange={(e) => {
                                      handleQuantityChange(e.target.checked ? item.original_quantity : 0);
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <div>
                                    <p className="font-medium">{item.name}</p>
                                    {item.description && (
                                      <p className="text-xs text-muted-foreground">{item.description}</p>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-right">
                                  {item.original_quantity}
                                </TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(item.unit_price)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {item.vat_rate}%
                                </TableCell>
                                <TableCell className="text-right">
                                  <Input
                                    type="number"
                                    min="0"
                                    max={item.original_quantity}
                                    step="0.01"
                                    value={item.credit_quantity || 0}
                                    onChange={(e) => {
                                      const value = parseFloat(e.target.value) || 0;
                                      handleQuantityChange(value);
                                    }}
                                    className="w-24 text-right"
                                    disabled={!item.selected}
                                  />
                                </TableCell>
                                <TableCell className="text-right font-semibold">
                                  {formatCurrency(item.total_amount || 0)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                  
                  {/* Totals */}
                  <div className="flex justify-end space-x-4 pt-4 border-t">
                    <div className="text-right space-y-1">
                      <p className="text-sm text-muted-foreground">
                        Celkom bez DPH: {formatCurrency(
                          creditNoteItems
                            .filter((item: any) => item.selected && item.credit_quantity > 0)
                            .reduce((sum, item) => sum + (item.amount || 0), 0)
                        )}
                      </p>
                      {user?.isVatPayer === true && (
                        <p className="text-sm text-muted-foreground">
                          DPH: {formatCurrency(
                            creditNoteItems
                              .filter((item: any) => item.selected && item.credit_quantity > 0)
                              .reduce((sum, item) => sum + (item.vat_amount || 0), 0)
                          )}
                        </p>
                      )}
                      <p className="text-lg font-bold">
                        Celkom: {formatCurrency(
                          creditNoteItems
                            .filter((item: any) => item.selected && item.credit_quantity > 0)
                            .reduce((sum, item) => sum + (item.total_amount || 0), 0)
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Reason */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold">Dôvod dobropisu *</label>
                  <Select value={creditNoteReason} onValueChange={setCreditNoteReason}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Vyberte dôvod" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="storno_objednavky">Storno objednávky</SelectItem>
                      <SelectItem value="vratenie_tovaru">Vrátenie tovaru</SelectItem>
                      <SelectItem value="oprava_chyby">Oprava chyby</SelectItem>
                      <SelectItem value="inak">Iný dôvod</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Popis dôvodu..."
                    value={creditNoteReasonText}
                    onChange={(e) => setCreditNoteReasonText(e.target.value)}
                    required
                  />
                </div>
                
                {/* Notes */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold">Poznámky</label>
                  <Textarea
                    value={creditNoteNotes}
                    onChange={(e) => setCreditNoteNotes(e.target.value)}
                    className="min-h-[80px]"
                    placeholder="Voliteľné poznámky..."
                  />
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreditNoteDialogOpen(false);
                  setCreditNoteInvoice(null);
                  setCreditNoteItems([]);
                }}
              >
                Zrušiť
              </Button>
              <Button
                onClick={handleSubmitCreditNote}
                disabled={createCreditNoteMutation.isPending || creditNoteItems.filter((item: any) => item.selected && item.credit_quantity > 0).length === 0}
              >
                {createCreditNoteMutation.isPending ? 'Vytváram...' : 'Vytvoriť dobropis'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// Komponent pre zobrazenie obsahu faktúry bez iframe
function InvoicePreviewContent({ invoiceId, invoiceNumber }: { invoiceId: number, invoiceNumber: string }) {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInvoiceHtml = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await fetch(`/api/invoices/${invoiceId}/html?t=${Date.now()}`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const html = await response.text();
        setHtmlContent(html);
      } catch (err) {
        console.error('Error fetching invoice HTML:', err);
        setError(err instanceof Error ? err.message : 'Nepodarilo sa načítať faktúru');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoiceHtml();
  }, [invoiceId]);

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p>Načítavam faktúru...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center text-red-600">
          <p>Chyba pri načítaní faktúry:</p>
          <p className="text-sm mt-2">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="w-full h-full overflow-auto p-4"
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
}

export default Invoices;
